<template>
  <div v-if="!dataSourcesSupported">
    <div class="section-header"><h4>How to use</h4></div>
    <div class="how-to-use">
      <BaseAlert size="small" state="info">
        This integration is available to AI agents. Open an agent conversation
        and ask it to use this integration.
      </BaseAlert>
      <ol class="how-to-use-steps">
        <li>Navigate to an agent from the sidebar.</li>
        <li>Start a new conversation.</li>
        <li>Ask the agent to perform a task using this integration.</li>
      </ol>
    </div>
  </div>

  <div v-else>
    <div class="section-header">
      <h4>Data Sources</h4>
      <BaseButton
        kind="main"
        type="secondary"
        text="Add"
        :state="connections.length === 0 ? 'disabled' : 'default'"
        :tooltip="
          connections.length === 0
            ? 'Add an account before adding a data source'
            : undefined
        "
        tooltip-display="always"
        @click="onAdd()"
      />
    </div>
    <BaseAlert
      size="small"
      state="info"
      v-if="enabledDataCollections.length === 0"
    >
      Data sources are synced from the integration. You can add multiple per
      integration.
    </BaseAlert>
    <div class="sources-list">
      <template
        v-for="(item, index) in enabledDataCollections"
        :key="getSourceKey(item.dataCollectionId)"
      >
        <div v-if="index > 0" class="row-separator" />
        <div
          class="source-row data-row"
          :class="{ clickable: !!getDatabaseId(item.dataCollection) }"
          @click="
            getDatabaseId(item.dataCollection) &&
            onOpenDatabase(
              item.workspaceId,
              getDatabaseId(item.dataCollection)!,
              $event
            )
          "
        >
          <div class="source-name">
            {{ item.databaseName }}

            <Spinner
              v-if="getDataCollectionStatus(item.dataCollection) === 'pending'"
              v-tippy="{
                content: getDataCollectionStatusText(item.dataCollection),
                arrow: false,
                placement: 'top',
              }"
              class="data-collection-status-spinner"
            />
            <div
              v-else
              class="data-collection-status-dot"
              :class="{
                ready: getDataCollectionStatus(item.dataCollection) === 'ready',
                error: getDataCollectionStatus(item.dataCollection) === 'error',
              }"
              v-tippy="{
                content: getDataCollectionStatusText(item.dataCollection),
                arrow: false,
                placement: 'top',
              }"
            />
            <span
              v-if="getSubStatusIndicator(item.dataCollection)"
              :title="getSubStatusIndicator(item.dataCollection)!.tooltip"
              class="sub-status-icon"
            >
              <Spinner
                v-if="
                  getDataCollectionStatus(item.dataCollection) === 'pending'
                "
                v-tippy="{
                  content: getDataCollectionStatusText(item.dataCollection),
                  arrow: false,
                  placement: 'top',
                }"
                class="data-collection-status-spinner"
              />
              <InfoIcon
                v-if="
                  getSubStatusIndicator(item.dataCollection)!.icon === 'info'
                "
              />
              <WarningIcon
                v-else-if="
                  getSubStatusIndicator(item.dataCollection)!.icon === 'warning'
                "
              />
            </span>
          </div>
          <div class="actions" @click.stop>
            <ButtonMenu :offset="[0, 4]" placement="bottom-end">
              <template #btn>
                <BaseButton kind="auxiliary">
                  <template #icon><MoreIcon /></template>
                </BaseButton>
              </template>
              <template #content>
                <BaseButton
                  v-if="getDatabaseId(item.dataCollection)"
                  class="context-action"
                  kind="auxiliary"
                  text="See objects"
                  is-expanded
                  @click="
                    onOpenDatabase(
                      item.workspaceId,
                      getDatabaseId(item.dataCollection)!,
                      $event
                    )
                  "
                >
                  <template #icon><DatabaseIcon /></template>
                </BaseButton>
                <BaseButton
                  v-if="item.dataCollection.status === 'ready'"
                  class="context-action"
                  kind="auxiliary"
                  text="Refresh"
                  is-expanded
                  @click="
                    onForceRefresh(
                      item.workspaceId,
                      item.dataCollectionId,
                      item.dataCollection
                    )
                  "
                >
                  <template #icon><RefreshIcon /></template>
                </BaseButton>
                <BaseButton
                  class="context-action"
                  kind="auxiliary"
                  text="Delete"
                  tint="red"
                  is-expanded
                  @click="
                    onDeleteSource(
                      item.workspaceId,
                      item.dataCollectionId,
                      item.dataCollection
                    )
                  "
                >
                  <template #icon><DeleteIcon /></template>
                </BaseButton>
              </template>
            </ButtonMenu>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { Connection } from "@membranehq/sdk";
import { match, P } from "ts-pattern";
import { defineComponent, PropType } from "vue";

import type {
  IntegrationAppEnabledDataCollection,
  IntegrationAppEnabledDataCollectionId,
} from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import DatabaseIcon from "@/components/icons/DatabaseIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import RefreshIcon from "@/components/icons/RefreshIcon.vue";
import WarningIcon from "@/components/icons/WarningIcon.vue";
import {
  dataCollectionLastSynced,
  lastSyncedText,
} from "@/components/settings/integrations/dataCollection";
import Spinner from "@/components/Spinner.vue";

import type { EnabledDataCollectionWithWorkspace } from "./useMembraneSetup";

export default defineComponent({
  components: {
    BaseAlert,
    BaseButton,
    ButtonMenu,
    DatabaseIcon,
    DeleteIcon,
    InfoIcon,
    MoreIcon,
    RefreshIcon,
    Spinner,
    WarningIcon,
  },
  props: {
    enabledDataCollections: {
      type: Array as PropType<EnabledDataCollectionWithWorkspace[]>,
      required: true,
    },
    connections: {
      type: Array as PropType<Connection[]>,
      required: true,
    },
    dataSourcesSupported: {
      type: Boolean,
      required: true,
    },
    onAdd: {
      type: Function as PropType<() => void>,
      required: true,
    },
    onDeleteSource: {
      type: Function as PropType<
        (
          workspaceId: string,
          dataCollectionId: IntegrationAppEnabledDataCollectionId,
          dataCollection: IntegrationAppEnabledDataCollection
        ) => void | Promise<void>
      >,
      required: true,
    },
    onForceRefresh: {
      type: Function as PropType<
        (
          workspaceId: string,
          dataCollectionId: IntegrationAppEnabledDataCollectionId,
          dataCollection: IntegrationAppEnabledDataCollection
        ) => void | Promise<void>
      >,
      required: true,
    },
    onOpenDatabase: {
      type: Function as PropType<
        (
          workspaceId: string,
          databaseId: string,
          event: Event
        ) => void | Promise<void>
      >,
      required: true,
    },
  },
  setup() {
    const getSourceKey = (id: IntegrationAppEnabledDataCollectionId) => {
      const s = JSON.stringify({
        connection: id.connection_id,
        collection: id.data_collection_key,
        params: id.parameters,
      });
      let hash = 0;
      for (let i = 0; i < s.length; i++) {
        hash = (hash << 5) - hash + s.charCodeAt(i);
        hash = hash & hash;
      }
      return hash.toString();
    };

    const getDataCollectionStatus = (
      dc: IntegrationAppEnabledDataCollection
    ): "ready" | "pending" | "error" =>
      match(dc)
        .with({ status: "ready" }, () => "ready" as const)
        .with({ status: "creation_pending" }, () => "pending" as const)
        .with({ status: "force_refresh" }, () => "pending" as const)
        .with({ status: "error" }, () => "error" as const)
        .exhaustive();

    const getDataCollectionStatusText = (
      dc: IntegrationAppEnabledDataCollection
    ) => {
      const synced = lastSyncedText(dataCollectionLastSynced(dc));
      const suffix = synced ? ` — ${synced}` : "";
      return match(dc)
        .with({ status: "ready" }, () => `Data source is synchronized${suffix}`)
        .with(
          { status: "creation_pending" },
          () => "Data source is being created"
        )
        .with(
          { status: "force_refresh" },
          () => `Data source is refreshing${suffix}`
        )
        .with(
          { status: "error" },
          () =>
            "Data source has an error, contact Routine support if it persists"
        )
        .exhaustive();
    };

    const getDatabaseId = (
      dc: IntegrationAppEnabledDataCollection
    ): string | null =>
      match(dc)
        .with({ status: "ready" }, { status: "force_refresh" }, (d) => d.table)
        .with({ status: "creation_pending" }, () => null)
        .with({ status: "error" }, (d) =>
          match(d.last_known_state)
            .with({ status: "ready" }, (s) => s.table)
            .with({ status: "creation_pending" }, () => null)
            .exhaustive()
        )
        .exhaustive();

    const getSubStatusIndicator = (
      dc: IntegrationAppEnabledDataCollection
    ): { icon: "info" | "warning"; tooltip: string } | null => {
      if (dc.status !== "ready") return null;
      const paginationStoppedHandler = (max: number) => ({
        icon: "warning" as const,
        tooltip: `This data source has reached the maximum number of objects (${max}) and additional objects will not be synchronized.`,
      });
      return match(dc.sub_status)
        .with({ "status-kind": "success" }, "success", () => null)
        .with({ "status-kind": "no_list_method" }, "no_list_method", () => ({
          icon: "info" as const,
          tooltip:
            "This data source cannot fetch external objects, but creation/update/deletion might still work.",
        }))
        .with(
          {
            "status-kind": "pagination_stopped",
            "pagination-stopped-reason-kind": "max_number_of_records",
            max_number_of_records: P.select(),
          },
          paginationStoppedHandler
        )
        .with(P.number, paginationStoppedHandler)
        .exhaustive();
    };

    return {
      getSourceKey,
      getDataCollectionStatus,
      getDataCollectionStatusText,
      getDatabaseId,
      getSubStatusIndicator,
    };
  },
});
</script>

<style scoped lang="scss">
h4 {
  margin-bottom: 4px;
  font-weight: 500;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.how-to-use {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.how-to-use-steps {
  @extend %font-caption-regular;
  margin: 0;
  padding-inline-start: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    @include themed() {
      color: t($grey-70);
    }
  }
}

.sources-list {
  display: flex;
  flex-direction: column;
}

.row-separator {
  height: 1px;
  @include themed() {
    background-color: t($grey-10);
  }
}

.data-row {
  padding: 10px 4px;

  &:hover {
    @include transition(background-color 0.15s ease-out);
    @include themed() {
      background-color: t($grey-03);
    }
  }
}

.context-action {
  width: 100%;
}

.clickable:hover {
  @include themed() {
    background-color: t($grey-03);
  }
}

.source-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  column-gap: 12px;
  padding: 6px 4px;

  .source-name {
    @extend %font-body-regular;
    display: flex;
    align-items: center;
    gap: 8px;

    .sub-status-icon {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      cursor: default;
      @include themed() {
        color: t($grey-50);
      }
    }
  }

  .data-collection-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-inline-start: 4px;
    &.ready {
      background-color: #10b981;
    }
    &.error {
      background-color: #ef4444;
    }
  }

  .data-collection-status-spinner {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    margin-inline-start: 4px;
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }
}
</style>
